import React from "react";
import { Button } from "@/components/ui/button";
import { logClientError } from "@/lib/errorLogger";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App crashed:", error);
    console.error("Component stack:", errorInfo.componentStack);
    void logClientError(error.message, `${error.stack}\n\nComponent stack:\n${errorInfo.componentStack}`);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoBack = () => {
    window.history.back();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-lg border bg-card p-6 space-y-4">
          <header className="space-y-1">
            <h1 className="text-lg font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              The page crashed. You can go back or reload to continue.
            </p>
          </header>

          {import.meta.env.DEV && this.state.error?.message && (
            <pre className="text-xs bg-muted/50 border rounded p-3 overflow-auto">
              {this.state.error.message}
            </pre>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={this.handleGoBack}>
              Go back
            </Button>
            <Button onClick={this.handleReload}>Reload</Button>
          </div>
        </div>
      </div>
    );
  }
}
